import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X, Link } from 'lucide-react';
import { useGame } from '../context/GameContext';

const ConfirmConnectionModal: React.FC = () => {
  const { gameState, actions } = useGame();
  const isOpen = !!gameState.pendingConnection;
  
  if (!gameState.pendingConnection) return null;

  const { word1, word2 } = gameState.pendingConnection;

  const handleConfirm = () => {
    actions.confirmConnection();
  };

  const handleCancel = () => {
    actions.hideConfirmationDialog();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCancel}
        >
          <motion.div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Link className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Confirm Connection</h2>
            </div>

            {/* Content */}
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Do you want to connect these words?
              </p>
              <div className="flex items-center justify-center gap-4 p-4 bg-gray-50 rounded-xl">
                <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold text-lg">
                  {word1.toUpperCase()}
                </span>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold text-lg">
                  {word2.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <motion.button
                onClick={handleCancel}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <X className="w-4 h-4" />
                Cancel
              </motion.button>
              <motion.button
                onClick={handleConfirm}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CheckCircle className="w-4 h-4" />
                Connect
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmConnectionModal;